import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Plus } from "lucide-react";
import api from "@/lib/api";
import { SUBCATEGORIES } from "@/lib/categories";
import { useLocation } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import { SeekerCard } from "@/components/SeekerCard";

const JOB_TYPES = ["Full-time", "Part-time", "Vikarie", "Praktikë"];
const EXPERIENCE = ["Pa përvojë", "Me përvojë"];
const SORTS = [{ k: "newest", l: "Më të rejat" }, { k: "distance", l: "Më afër" }, { k: "profession", l: "Sipas profesionit" }];

export default function KerkoPunePage() {
  const nav = useNavigate();
  const { loc } = useLocation();
  const { user, setAuthOpen } = useAuth();
  const [all, setAll] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prof, setProf] = useState("");
  const [city, setCity] = useState("");
  const [profFilter, setProfFilter] = useState("");
  const [jobType, setJobType] = useState("");
  const [exp, setExp] = useState("");
  const [sort, setSort] = useState("newest");

  const fetchData = () => {
    setLoading(true);
    const params = new URLSearchParams({ category: "punekerkues" });
    if (prof) params.set("q", prof);
    if (profFilter) params.set("subcategory", profFilter);
    if (sort === "distance") params.set("sort", "distance");
    if (loc.coords) { params.set("lat", loc.coords.lat); params.set("lng", loc.coords.lng); }
    api.get(`/listings?${params.toString()}`).then((r) => setAll(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(fetchData, [profFilter, sort, loc.coords]);
  useEffect(() => { api.get("/favorites/ids").then((f) => setSavedIds(f.data)).catch(() => {}); }, []);

  let results = all.filter((s) => {
    const d = s.data || {};
    if (city && !(s.city || "").toLowerCase().includes(city.toLowerCase())) return false;
    if (jobType && !(d.job_types || []).includes(jobType)) return false;
    if (exp && d.experience_level !== exp) return false;
    return true;
  });
  if (sort === "profession") results = [...results].sort((a, b) => (a.subcategory || "").localeCompare(b.subcategory || ""));

  const createProfile = () => { if (!user || user === false) { setAuthOpen(true); return; } nav("/krijo-profil-pune"); };

  return (
    <div className="gs-fade">
      <div className="bg-gradient-to-br from-yellow-400 to-amber-500 text-white px-4 pt-3 pb-5 rounded-b-3xl">
        <button data-testid="back-button" onClick={() => nav(-1)} className="p-1.5 -ml-1 mb-2 rounded-full bg-white/20"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-extrabold">🔎 Kërko Punë</h1>
        <div className="mt-3 space-y-2">
          <input data-testid="kerko-prof" value={prof} onChange={(e) => setProf(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchData()} placeholder="Çfarë pune po kërkon?" className="w-full bg-white text-slate-800 rounded-xl px-4 h-11 outline-none text-sm" />
          <input data-testid="kerko-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="📍 Në cilin qytet po kërkon?" className="w-full bg-white text-slate-800 rounded-xl px-4 h-11 outline-none text-sm" />
          <button data-testid="kerko-submit" onClick={fetchData} className="w-full bg-slate-900 text-white rounded-xl h-11 font-bold flex items-center justify-center gap-2"><Search className="w-4 h-4" />Kërko</button>
        </div>
      </div>

      <div className="px-4 mt-3">
        <button data-testid="add-seeker-profile" onClick={createProfile} className="w-full flex items-center justify-center gap-2 bg-amber-50 text-amber-700 font-semibold rounded-2xl py-3 border border-amber-200"><Plus className="w-4 h-4" />Shto profilin tim si punëkërkues</button>

        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mt-3 pb-1">
          {JOB_TYPES.map((t) => <button key={t} data-testid={`jt-${t}`} onClick={() => setJobType(jobType === t ? "" : t)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${jobType === t ? "bg-yellow-500 text-white border-transparent" : "bg-yellow-50 text-yellow-700 border-yellow-200"}`}>{t}</button>)}
          {EXPERIENCE.map((t) => <button key={t} data-testid={`ex-${t}`} onClick={() => setExp(exp === t ? "" : t)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${exp === t ? "bg-amber-500 text-white border-transparent" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{t}</button>)}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mt-2 pb-1">
          {SUBCATEGORIES.pune.slice(0, 12).map((s) => <button key={s} onClick={() => setProfFilter(profFilter === s ? "" : s)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${profFilter === s ? "bg-slate-900 text-white border-transparent" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{s}</button>)}
        </div>
        <select data-testid="kerko-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="mt-2 text-sm bg-white border border-slate-200 rounded-xl px-3 py-2 font-medium text-slate-700 outline-none">{SORTS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}</select>
      </div>

      <div className="px-4 mt-3">
        {loading ? <p className="text-center text-slate-400 py-10">Duke ngarkuar...</p>
          : results.length ? <div className="grid grid-cols-2 gap-3">{results.map((s) => <SeekerCard key={s.id} seeker={s} saved={savedIds.includes(s.id)} />)}</div>
          : <div className="text-center py-12"><p className="text-slate-400">Nuk u gjet asnjë punëkërkues.</p><button onClick={createProfile} className="mt-3 text-amber-600 font-semibold">Bëhu i pari — shto profilin tënd</button></div>}
      </div>
    </div>
  );
}
